import {createEntityMeta,SyncStatus} from '../offline/entity.js';

export const WOOD_FAVOURITE_ENTITY_TYPE='wood_favourite';

function requiredKey(value,label,max=128){
  const key=String(value||'').trim().toLowerCase();
  if(!key||key.length>max)throw new TypeError(`${label} is required`);
  return key;
}

export function woodFavouriteId(userId,woodKey){
  return `${requiredKey(userId,'userId')}:${requiredKey(woodKey,'woodKey',64)}`;
}

export function createWoodFavourite({userId,woodKey,createdAt=new Date().toISOString()}={}){
  const user=requiredKey(userId,'userId');
  const wood=requiredKey(woodKey,'woodKey',64);
  return Object.freeze({
    id:woodFavouriteId(user,wood),
    userId:user,
    woodKey:wood,
    createdAt:String(createdAt)
  });
}

export function woodFavouriteToRow(value){
  const f=createWoodFavourite(value);
  return Object.freeze({user_id:f.userId,wood_key:f.woodKey});
}

export function woodFavouriteFromRow(row={}){
  return createWoodFavourite({
    userId:row.user_id,
    woodKey:row.wood_key,
    createdAt:row.created_at
  });
}

export function createWoodFavouriteStore({repository,queue,now=()=>new Date().toISOString()}={}){
  if(!repository?.get||!repository?.list||!repository?.put)throw new TypeError('repository with get/list/put is required');
  if(!queue?.enqueue)throw new TypeError('sync queue with enqueue is required');

  async function list(userId){
    const user=requiredKey(userId,'userId');
    const rows=await repository.list(WOOD_FAVOURITE_ENTITY_TYPE);
    return Object.freeze((rows||[])
      .filter(row=>row.userId===user&&row.syncStatus!==SyncStatus.PENDING_DELETE)
      .sort((a,b)=>String(a.woodKey).localeCompare(String(b.woodKey))));
  }

  async function isFavourite(userId,woodKey){
    const row=await repository.get(WOOD_FAVOURITE_ENTITY_TYPE,woodFavouriteId(userId,woodKey));
    return Boolean(row&&row.syncStatus!==SyncStatus.PENDING_DELETE);
  }

  async function add(userId,woodKey){
    const favourite=createWoodFavourite({userId,woodKey,createdAt:now()});
    const existing=await repository.get(WOOD_FAVOURITE_ENTITY_TYPE,favourite.id);
    if(existing&&existing.syncStatus!==SyncStatus.PENDING_DELETE)return existing;

    const entity={
      ...createEntityMeta({id:favourite.id,now:now(),syncStatus:SyncStatus.PENDING_CREATE}),
      ...favourite,
      deletedAt:null
    };
    return queue.enqueue(WOOD_FAVOURITE_ENTITY_TYPE,entity,'create');
  }

  async function remove(userId,woodKey){
    const id=woodFavouriteId(userId,woodKey);
    const existing=await repository.get(WOOD_FAVOURITE_ENTITY_TYPE,id);
    if(!existing)return Object.freeze({ok:true,status:'already_absent',id});
    const removed=await queue.enqueue(WOOD_FAVOURITE_ENTITY_TYPE,existing,'delete');
    return Object.freeze({ok:true,status:'pending_delete',id,entity:removed});
  }

  async function toggle(userId,woodKey){
    return (await isFavourite(userId,woodKey))
      ?remove(userId,woodKey)
      :add(userId,woodKey);
  }

  return Object.freeze({list,isFavourite,add,remove,toggle});
}
